import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Meta from '../components/Meta';
import { PlanningContextProvider, usePlanningContext } from '../components/planning/state/PlanningContext';
import GroupedApplicationsCard from '../components/ui/GroupedApplicationsCard';
import ImageModal from '../components/planning/ImageModal';
import { Star, Calendar } from 'lucide-react';
import { getInterestScore } from '../src/lib/interestScore';
import { getPlanningFeedAddedDate } from '../utils/addedToday';

const normalizeAddress = (str = '') => str.toLowerCase().replace(/\s+/g,' ').trim()
  .replace(/^(flat|apartment|apt|unit)\s+\d+[a-z]*,?\s*/i, '')
  .replace(/^\d+[a-z]*[f/-]\d+[a-z]*,?\s*/i, '')
  .replace(/^pf\d+\s*,?\s*/i, '');

const hasInterestDecision = (app) => typeof app?.interesting === 'boolean';
const getBackendInterestScore = (app) => {
  const confidence = Number(app?.interest_confidence);
  const normalizedConfidence = Number.isFinite(confidence)
    ? Math.max(0, Math.min(1, confidence))
    : 0.75;
  const scaleBonus = Number.isFinite(Number(app?.scale_rating)) ? Number(app.scale_rating) : 0;
  return Math.round(normalizedConfidence * 100 + scaleBonus);
};

const WeeklyContent = () => {
  const { applications } = usePlanningContext();
  const [selectedImage, setSelectedImage] = useState(null);

  const weeklyGroups = useMemo(() => {
    const isWithinLastWeek = (app) => {
      const addedDate = getPlanningFeedAddedDate(app);
      if (!addedDate) return false;
      const date = new Date(addedDate);
      if (Number.isNaN(date.getTime())) return false;
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return date >= weekAgo && date <= now;
    };

    const recentApps = (applications || []).filter(app => isWithinLastWeek(app));

    const scored = recentApps.map(app => {
      if (app?.interesting === true) {
        return { ...app, interestScore: getBackendInterestScore(app), interestSource: 'backend' };
      }
      if (hasInterestDecision(app)) return null;

      const base = getInterestScore(app);
      const addedDate = getPlanningFeedAddedDate(app);
      const d = addedDate ? new Date(addedDate) : new Date();
      const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
      const recencyMultiplier = 1 + (0.4 / (1 + Math.exp(days - 3)));
      const scaleBonus = (app.scale_rating ?? 0) * 2;
      const interestScore = Math.round(base * recencyMultiplier + scaleBonus);
      return { ...app, interestScore, interestSource: 'fallback' };
    }).filter(Boolean);

    const buckets = {};
    scored.forEach(app => {
      const key = app.group_id || normalizeAddress(app.address || app.location || '');
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(app);
    });

    return Object.values(buckets)
      .map(apps => ({ baseAddress: apps[0]?.address, apps }))
      .sort((a, b) => (b.apps[0]?.interestScore || 0) - (a.apps[0]?.interestScore || 0));
  }, [applications]);

  return (
    <div className="min-h-screen bg-[#0D0F13] text-white">
      <Meta title="Weekly Highlights | City Scope" description="Top planning applications this week" />
      <div className="relative z-10 container mx-auto px-6 py-8 pt-[calc(var(--navbar-height,64px)+1.5rem)]">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold uppercase tracking-wide">Weekly Highlights</h1>
              <p className="text-[var(--c-text-secondary)]">Top applications first seen in the last 7 days</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-3 py-2 rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[var(--c-accent)]" />
                <span className="font-mono text-sm">Last 7 days</span>
              </div>
              <div className="px-3 py-2 rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] flex items-center gap-2">
                <Star className="w-4 h-4 text-[var(--c-accent)]" />
                <span className="font-mono text-sm">{weeklyGroups.length} groups</span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {weeklyGroups.map((group, index) => (
            <GroupedApplicationsCard
              key={index}
              group={group}
              showAiSummaries={true}
              setSelectedImage={setSelectedImage}
            />
          ))}
          {weeklyGroups.length === 0 && (
            <div className="col-span-full text-[var(--c-text-secondary)]">No highlights this week.</div>
          )}
        </div>
      </div>

      {selectedImage && (
        <ImageModal selectedImage={selectedImage} setSelectedImage={setSelectedImage} themeMode="dark" />
      )}
    </div>
  );
};

const FeaturedThisWeek = () => (
  <PlanningContextProvider initialApplications={[]}> 
    <WeeklyContent />
  </PlanningContextProvider>
);

export default FeaturedThisWeek;
